// Sistema de toasts (port de toast.js) como contexto React. Markup/clases 1:1.
import { createContext, useContext, useCallback, useState, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

type Tipo = 'success' | 'error' | 'warning' | 'info';
interface ToastItem { id: number; tipo: Tipo; titulo: string; mensaje?: string; leaving?: boolean; }

const ICONS: Record<Tipo, string> = {
  success: 'fa-circle-check',
  error: 'fa-circle-xmark',
  warning: 'fa-triangle-exclamation',
  info: 'fa-circle-info',
};

interface ToastApi {
  success: (titulo: string, mensaje?: string) => void;
  error: (titulo: string, mensaje?: string) => void;
  warning: (titulo: string, mensaje?: string) => void;
  info: (titulo: string, mensaje?: string) => void;
}

const Ctx = createContext<ToastApi>({ success() {}, error() {}, warning() {}, info() {} });

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const remove = useCallback((id: number) => {
    setItems((xs) => xs.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    setTimeout(() => setItems((xs) => xs.filter((t) => t.id !== id)), 220);
  }, []);

  const push = useCallback((tipo: Tipo, titulo: string, mensaje?: string) => {
    const id = nextId.current++;
    setItems((xs) => [...xs, { id, tipo, titulo, mensaje }]);
    setTimeout(() => remove(id), tipo === 'error' ? 6000 : 4000);
  }, [remove]);

  const api: ToastApi = {
    success: (t, m) => push('success', t, m),
    error: (t, m) => push('error', t, m),
    warning: (t, m) => push('warning', t, m),
    info: (t, m) => push('info', t, m),
  };

  return (
    <Ctx.Provider value={api}>
      {children}
      {createPortal(
        <div className="toast-stack">
          {items.map((t) => (
            <div key={t.id} className={`toast ${t.tipo}${t.leaving ? ' out' : ''}`}>
              <i className={`fa-solid ${ICONS[t.tipo]} lead`} />
              <div className="t-body">
                <b>{t.titulo}</b>
                {t.mensaje ? <span>{t.mensaje}</span> : null}
              </div>
              <button className="close" onClick={() => remove(t.id)} aria-label="Cerrar">
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  return useContext(Ctx);
}
